import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';

type Summary = {
  id: number; subject: string; sentAt: string; preview: string; read: boolean;
  from?: { id: number; name: string };
  to?: string[];
};
type Conversation = {
  rootId: number; subject: string;
  messages: {
    id: number; body: string; sentAt: string; mine: boolean;
    from: { id: number; name: string };
    to: { id: number; name: string }[];
  }[];
};
type Person = { id: number; name: string; login: string };

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Messages.
 *
 * The list and the conversation sit side by side rather than on two screens,
 * because reading a message and replying to it is one job.
 */
export function Messages() {
  const [box, setBox] = useState<'inbox' | 'sent'>('inbox');
  const [items, setItems] = useState<Summary[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setItems((await api.get<{ items: Summary[] }>(`/messages?box=${box}`)).items); }
    catch (e) { setError((e as ApiError).message); }
  }, [box]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Messages</h1>
        <button className="btn btn--primary" onClick={() => { setWriting(true); setOpenId(null); }}>
          Write a message
        </button>
      </div>

      {error && <p className="auth__error">{error}</p>}

      <div className="tabs">
        {(['inbox', 'sent'] as const).map((b) => (
          <button key={b} className={`tabs__tab ${box === b ? 'is-on' : ''}`}
            onClick={() => { setBox(b); setOpenId(null); setWriting(false); }}>
            {b === 'inbox' ? 'Inbox' : 'Sent'}
          </button>
        ))}
      </div>

      <div className="split">
        <div className="split__list">
          {items === null && <p className="muted">Loading…</p>}
          {items?.length === 0 && (
            <p className="notice">{box === 'inbox' ? 'Nothing in your inbox.' : 'You have not sent anything yet.'}</p>
          )}
          {items?.map((m) => (
            <button key={m.id}
              className={`mailrow ${openId === m.id ? 'mailrow--on' : ''} ${m.read ? '' : 'mailrow--unread'}`}
              onClick={() => { setOpenId(m.id); setWriting(false); }}>
              <span className="mailrow__who">
                {box === 'inbox' ? m.from?.name : (m.to?.join(', ') || '—')}
              </span>
              <span className="mailrow__subject">{m.subject}</span>
              <span className="mailrow__preview">{m.preview}</span>
              <span className="mailrow__when">{when(m.sentAt)}</span>
            </button>
          ))}
        </div>

        <div className="split__detail">
          {writing && <Compose onSent={() => { setWriting(false); setBox('sent'); void load(); }}
            onCancel={() => setWriting(false)} />}
          {!writing && openId !== null && (
            <Thread id={openId} onChanged={() => void load()} onGone={() => { setOpenId(null); void load(); }} />
          )}
          {!writing && openId === null && <p className="muted">Choose a message to read it.</p>}
        </div>
      </div>
    </>
  );
}

function Thread({ id, onChanged, onGone }: { id: number; onChanged: () => void; onGone: () => void }) {
  const [data, setData] = useState<Conversation | null>(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<Conversation>(`/messages/${id}`)); onChanged(); }
    catch (e) { setError((e as ApiError).message); }
    // onChanged is a refresh of the list behind; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { setReply(''); setError(null); void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const last = data.messages[data.messages.length - 1];
  // Reply goes to everybody else on the last message: its sender and the other
  // recipients, minus me. The server checks this again.
  const replyTo = [...new Set([
    ...(last.mine ? [] : [last.from.id]),
    ...last.to.map((t) => t.id),
  ])];

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/messages', {
        to: replyTo, subject: data.subject.startsWith('Re:') ? data.subject : `Re: ${data.subject}`,
        body: reply, parentId: data.rootId,
      });
      setReply('');
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="panel">
      <div className="page-head">
        <h2 className="h2">{data.subject}</h2>
        <button className="btn btn--ghost" onClick={async () => {
          await api.del(`/messages/${id}`); onGone();
        }}>Delete</button>
      </div>

      {data.messages.map((m) => (
        <article key={m.id} className={`bubble ${m.mine ? 'bubble--mine' : ''}`}>
          <header className="bubble__head">
            <strong>{m.from.name}</strong>
            <span className="muted small">
              {' to '}{m.to.map((t) => t.name).join(', ')} · {when(m.sentAt)}
            </span>
          </header>
          <p className="bubble__body">{m.body}</p>
        </article>
      ))}

      <div className="form">
        <label><span>Reply</span>
          <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder={`Reply to ${last.mine ? last.to.map((t) => t.name).join(', ') : last.from.name}`} />
        </label>
        <button className="btn btn--primary" disabled={busy || !reply.trim() || !replyTo.length} onClick={send}>
          {busy ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </section>
  );
}

/**
 * Writing a new message. The recipient box only offers people the portal will
 * actually accept — the same rule decides both, so an offered name is never
 * refused afterwards.
 */
function Compose({ onSent, onCancel }: { onSent: () => void; onCancel: () => void }) {
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [chosen, setChosen] = useState<Person[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try { setPeople((await api.get<{ items: Person[] }>(`/messages/recipients?search=${encodeURIComponent(search)}`)).items); }
      catch { /* the list simply stays as it was */ }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const offered = useMemo(
    () => people.filter((p) => !chosen.some((c) => c.id === p.id)),
    [people, chosen],
  );

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/messages', { to: chosen.map((c) => c.id), subject, body });
      onSent();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="panel">
      <div className="page-head">
        <h2 className="h2">New message</h2>
        <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
      </div>

      {error && <p className="auth__error">{error}</p>}

      <div className="form">
        <label><span>To</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the people you can write to" />
        </label>

        {chosen.length > 0 && (
          <ul className="chips">
            {chosen.map((c) => (
              <li key={c.id}>
                {c.name}
                <button type="button" className="chip__x" aria-label={`Remove ${c.name}`}
                  onClick={() => setChosen(chosen.filter((x) => x.id !== c.id))}>×</button>
              </li>
            ))}
          </ul>
        )}

        {offered.length > 0 && (
          <div className="picklist">
            {offered.slice(0, 12).map((p) => (
              <button key={p.id} className="picklist__item" onClick={() => setChosen([...chosen, p])}>
                {p.name} <span className="muted small">{p.login}</span>
              </button>
            ))}
          </div>
        )}
        {people.length === 0 && search && <p className="muted small">Nobody you can write to matches that.</p>}

        <label><span>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} />
        </label>
        <label><span>Message</span>
          <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>

        <button className="btn btn--primary"
          disabled={busy || !chosen.length || !subject.trim() || !body.trim()} onClick={send}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </section>
  );
}
